import React, { useEffect, useRef, useState } from "react";
import { Button, Input, Spin, Tooltip } from "antd";
import { ArrowLeft, Check, Eye, Pencil, X } from "lucide-react";
import moment from "moment-timezone";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import {
  REACT_APP_API_TENANTS_URL,
  DOMAIN_ID,
  ORG_ID,
  AUTH_TOKEN,
} from "../../../constants";
import { PageHeader, PageShell, StatusPill, fmtDate, iconButtonStyle } from "../../../design-system/tableUi";
import { AN_COLORS, AN_MONO } from "../../../design-system/tokens";
import { PanelHead, card } from "../../../pages/enduser/ssh-proxy/sshProxyUi";
import EndpointDetailsUser from "./EndpointDetailsUser";
import LocalUsersTable from "./LocalUsersTable";
import EndpointAdminMenu, { setupRunning } from "./EndpointAdminMenu";
import { setupPollDelay } from "./adminCredential";
import { ConnectButton } from "./connect/ConnectDialog";
import { DecommissionBanner } from "./setupScan";
import RevealPasswordModal from "../../../pages/enduser/password-rotation/RevealPasswordModal";
import {
  endpointLifecycle,
  findInstance,
  findJumpServer,
  getAdminCredentialStatus,
  keptPasswords,
} from "../../../service/sshProxyService";
import { isAdminUser } from "../../../utils/helpers";

// One endpoint: a summary of the machine, then its local users. For
// administrators on a gateway endpoint the header's ⋮ menu holds what the old
// Gateway Access tab did (Add user, scan, gateway, admin credential, setup,
// password SSH exceptions, decommission).
//
// Old links may still carry ?defaultTab=… (and ?setup=1) from when this page
// had tabs; they are ignored and land here.

// The hand-off older callers read from localStorage.
const rememberEndpoint = (row) => {
  localStorage.setItem("endPoints", JSON.stringify(row));
  localStorage.setItem("details", JSON.stringify({ hostAddress: row.hostName, instanceId: row.instanceId }));
};

const readStored = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch {
    return null;
  }
};

// A machine counts as online when it (or, for a gateway-managed one, the
// gateway reaching it) answered within this many minutes.
const ONLINE_WITHIN_MIN = 20;

const EndpointDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const idParam = new URLSearchParams(location.search).get("id");

  // The endpoint's row. With ?id= it is loaded from the API; without one (an
  // old link) it falls back to the localStorage hand-off from the list.
  const [endPoints, setEndPoints] = useState(() => (idParam ? null : readStored("endPoints") || {}));
  // loading | ready | missing | error
  const [loadState, setLoadState] = useState(idParam ? "loading" : "ready");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!idParam) return undefined;
    let alive = true;
    setLoadState("loading");
    findInstance({
      instanceId: idParam,
      onSuccess: (row) => {
        if (!alive) return;
        if (!row) {
          setEndPoints(null);
          setLoadState("missing");
          return;
        }
        rememberEndpoint(row);
        setEndPoints(row);
        setLoadState("ready");
      },
      onFailure: () => alive && setLoadState("error"),
    });
    return () => {
      alive = false;
    };
  }, [idParam, reloadKey]);

  const ep = endPoints || {};
  const isAdmin = isAdminUser();
  const instanceId = ep.instanceId;
  const gatewayEndpoint = !!ep.jumpServerId;

  // How the gateway reaches the machine (admin account or authnull-svc) and
  // how its setup went. Administrators, gateway endpoints only; re-read while
  // setup runs, backing off (setupPollDelay: 3 s, 10 s after a minute, 30 s
  // after five).
  const [adminStatus, setAdminStatus] = useState(null);
  const [adminKey, setAdminKey] = useState(0);
  // Bumped to reload the Local users table (a scan or a setup finished).
  const [usersKey, setUsersKey] = useState(0);
  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => {
    setAdminStatus(null);
  }, [instanceId]);

  useEffect(() => {
    if (!isAdmin || !gatewayEndpoint || !instanceId || loadState !== "ready") return undefined;
    let alive = true;
    getAdminCredentialStatus({
      instanceId: Number(instanceId),
      onSuccess: (st) => alive && setAdminStatus(st),
      onFailure: () => {},
    });
    return () => {
      alive = false;
    };
  }, [isAdmin, gatewayEndpoint, instanceId, loadState, adminKey]);

  const running = setupRunning(adminStatus);
  // A setup that just finished queued a scan; the users follow.
  const wasRunning = useRef(false);
  // When the current round of polling began: setup starting to run, or being
  // queued again from the ⋮ menu (Re-run setup / Update admin credential).
  const runningSince = useRef(null);
  useEffect(() => {
    if (wasRunning.current && !running) setUsersKey((k) => k + 1);
    if (running && runningSince.current == null) runningSince.current = Date.now();
    if (!running) runningSince.current = null;
    wasRunning.current = running;
  }, [running]);
  useEffect(() => {
    if (!running) return undefined;
    const delay = setupPollDelay(Date.now() - (runningSince.current ?? Date.now()));
    const t = setTimeout(() => setAdminKey((k) => k + 1), delay);
    return () => clearTimeout(t);
  }, [running, adminStatus]);

  // Setup was just queued: show it as Running at once, then follow it.
  const onSetupQueued = () => {
    runningSince.current = Date.now();
    setAdminStatus((prev) => ({
      ...(prev || {}),
      mode: "admin",
      bootstrap: { status: "queued", createdAt: new Date().toISOString() },
    }));
    setAdminKey((k) => k + 1);
  };

  // The users list takes the endpoint as { hostAddress, instanceId }. Opened by
  // id it comes from the loaded row, never from whatever localStorage holds.
  const stateHostAddress = idParam
    ? endPoints
      ? { hostAddress: endPoints.hostName, instanceId: endPoints.instanceId }
      : null
    : readStored("details") ||
      (ep.instanceId ? { hostAddress: ep.hostName, instanceId: ep.instanceId } : null);

  // A field saved from the summary card: keep the row (and the hand-off) current.
  const patchEndpoint = (patch) =>
    setEndPoints((prev) => {
      const next = { ...(prev || {}), ...patch };
      localStorage.setItem("endPoints", JSON.stringify(next));
      return next;
    });

  const title =
    ep.hostName || stateHostAddress?.hostAddress || (idParam ? (loadState === "loading" ? "…" : `Endpoint #${idParam}`) : "Endpoint");
  const lifecycle = endpointLifecycle(ep.status);
  const subtitle =
    loadState === "ready"
      ? [ep.privateIp || ep.publicIp || ep.ipAddress, ep.osDetail || osLabel(ep.osname || ep.osName)].filter(Boolean).join(" · ")
      : undefined;

  let body;
  if (loadState === "loading") {
    body = (
      <div style={{ padding: 60, textAlign: "center" }}>
        <Spin tip="Loading endpoint…">
          <div style={{ minHeight: 40 }} />
        </Spin>
      </div>
    );
  } else if (loadState === "missing" || loadState === "error") {
    body = (
      <div style={{ ...card, padding: 24, display: "grid", gap: 10, justifyItems: "start" }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>
          {loadState === "missing" ? (
            <>
              No endpoint with id <span style={{ fontFamily: AN_MONO }}>{idParam}</span>
            </>
          ) : (
            <>
              Couldn't load endpoint <span style={{ fontFamily: AN_MONO }}>{idParam}</span>
            </>
          )}
        </span>
        <span style={{ fontSize: 13, color: AN_COLORS.muted }}>
          {loadState === "missing"
            ? "It may have been removed, or it belongs to another organisation."
            : "Check your connection and try again."}
        </span>
        <span style={{ display: "inline-flex", gap: 8 }}>
          {loadState === "error" && <Button onClick={() => setReloadKey((k) => k + 1)}>Try again</Button>}
          <Button onClick={() => navigate("/pam/endpoint")}>Back to Endpoints</Button>
        </span>
      </div>
    );
  } else {
    body = (
      <div key={ep.instanceId || "stored"} style={{ display: "grid", gap: 16 }}>
        {lifecycle && <DecommissionBanner status={ep.status} instanceId={ep.instanceId} />}
        <InstanceSummaryCard endPoints={ep} onPatch={patchEndpoint} adminStatus={isAdmin && gatewayEndpoint ? adminStatus : null} />
        {isAdmin && lifecycle && instanceId ? (
          <KeptPasswordsCard key={lifecycle} instanceId={instanceId} hostName={ep.hostName} />
        ) : null}
        {isAdmin && instanceId ? (
          <LocalUsersTable
            instanceId={instanceId}
            hostName={ep.hostName}
            readOnly={!!lifecycle}
            refreshKey={usersKey}
            addOpen={addOpen}
            onAddOpenChange={setAddOpen}
          />
        ) : (
          <section>
            <PanelHead
              title="Local users"
              desc="Local accounts on this machine. Accounts stay current automatically — the endpoint is re-scanned daily."
            />
            <EndpointDetailsUser stateHostAddress={stateHostAddress || {}} />
          </section>
        )}
      </div>
    );
  }

  return (
    <PageShell>
      <PageHeader
        eyebrow="PAM · Targets"
        title={title}
        subtitle={subtitle ? <span style={{ fontFamily: AN_MONO, fontSize: 12.5 }}>{subtitle}</span> : undefined}
        chips={loadState === "ready" && ep.status ? <EndpointStatusPill status={ep.status} /> : undefined}
        actions={
          <>
            <Button onClick={() => navigate("/pam/endpoint")} icon={<ArrowLeft size={14} style={{ verticalAlign: -2 }} />}>
              Endpoints
            </Button>
            {/* Renders nothing unless the person has targets on this endpoint. */}
            {loadState === "ready" && instanceId && <ConnectButton instanceId={instanceId} hostname={ep.hostName} />}
            {loadState === "ready" && isAdmin && gatewayEndpoint && (
              <EndpointAdminMenu
                endpoint={ep}
                lifecycle={lifecycle}
                adminStatus={adminStatus}
                onAddUser={() => setAddOpen(true)}
                onSetupQueued={onSetupQueued}
                onScanFinished={() => setUsersKey((k) => k + 1)}
                onGatewayChange={(next) => patchEndpoint({ jumpServerId: next || undefined })}
                onLifecycleChange={(status) => patchEndpoint({ status })}
              />
            )}
          </>
        }
      />
      {body}
    </PageShell>
  );
};

export default EndpointDetails;

// The rotated passwords a decommissioned (or decommissioning) endpoint leaves
// in the vault: the machine keeps them, and once its account list is gone this
// is the only place to reveal them. Administrators only; hidden when there are
// none or the list can't be read.
function KeptPasswordsCard({ instanceId, hostName }) {
  const [rows, setRows] = useState([]);
  const [revealing, setRevealing] = useState(null); // account

  useEffect(() => {
    let alive = true;
    setRows([]);
    keptPasswords({
      instanceId: Number(instanceId),
      onSuccess: (res) => alive && setRows(Array.isArray(res.passwords) ? res.passwords : []),
      onFailure: () => alive && setRows([]),
    });
    return () => {
      alive = false;
    };
  }, [instanceId]);

  if (!rows.length) return null;
  return (
    <div style={{ ...card, padding: 18 }}>
      <PanelHead
        title="Rotated passwords kept in the vault"
        desc="Authnull rotated these accounts' passwords. The machine keeps them and only Authnull knows them — if it stays in use, reveal and save them."
      />
      <div style={{ display: "grid" }}>
        {rows.map((r, i) => (
          <div
            key={r.account}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "8px 0",
              borderTop: i ? `1px solid ${AN_COLORS.line2}` : "none",
            }}
          >
            <span style={{ fontFamily: AN_MONO, fontSize: 13, color: AN_COLORS.ink, minWidth: 0, wordBreak: "break-all" }}>
              {r.account}
            </span>
            <span style={{ flex: 1, fontSize: 12.5, color: AN_COLORS.muted }}>
              Rotated {fmtDate(r.rotatedAt)}
              {r.unconfirmed ? " (rotation in progress)" : ""}
            </span>
            <Button size="small" icon={<Eye size={13} style={{ verticalAlign: -2 }} />} onClick={() => setRevealing(r.account)}>
              Reveal
            </Button>
          </div>
        ))}
      </div>
      <RevealPasswordModal
        open={!!revealing}
        endpointId={Number(instanceId)}
        account={revealing}
        hostname={hostName}
        onClose={() => setRevealing(null)}
      />
    </div>
  );
}

// "ubuntu" → "Ubuntu", and the numeric OS ids ("1", "2") to their family;
// empty when unknown.
const OS_IDS = { 1: "Windows", 2: "Linux" };
function osLabel(os) {
  const s = String(os || "").trim();
  if (OS_IDS[s]) return OS_IDS[s];
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
}

function EndpointStatusPill({ status }) {
  const s = String(status || "").trim().toLowerCase();
  if (s === "decommissioning") return <StatusPill tone="warn" label="Decommissioning" />;
  if (s === "decommissioned") return <StatusPill tone="neutral" label="Decommissioned" />;
  return <StatusPill status={status} />;
}

// Online / Offline from a last-active unix time, as a pill with the age in a
// tooltip. Agent endpoints keep their old thresholds: >20 min offline, >10 min
// offline (stale), otherwise online.
function AgentHeartbeat({ lastActive }) {
  const seen = Number(lastActive) > 0 ? moment.unix(Number(lastActive)) : null;
  if (!seen) return <StatusPill tone="neutral" label="Never reported" />;
  const diff = Math.max(0, moment().diff(seen, "minutes"));
  const tip = `Last active: ${seen.fromNow()} (${seen.format("YYYY-MM-DD HH:mm")})`;
  const pill =
    diff > ONLINE_WITHIN_MIN ? (
      <StatusPill tone="danger" label="Offline" />
    ) : diff > 10 ? (
      <StatusPill tone="warn" label="Offline" />
    ) : (
      <StatusPill tone="success" label="Online" />
    );
  return (
    <Tooltip title={tip}>
      <span>{pill}</span>
    </Tooltip>
  );
}

// The admin (sudo) account the gateway sets the machine up through, with how
// its latest setup went. An endpoint set up with authnull-svc has none.
function AdminAccountValue({ status }) {
  if (status.mode !== "admin") {
    return <span style={{ fontSize: 13, color: AN_COLORS.muted }}>None — reached through Authnull's service account</span>;
  }
  const b = status.bootstrap;
  const s = b?.status;
  let pill = null;
  if (s === "done") pill = <StatusPill tone="success" label="Done" />;
  else if (s === "queued" || s === "claimed") pill = <StatusPill tone="info" label="Running" />;
  else if (s === "failed" || s === "expired")
    pill = (
      <Tooltip title={b.error || (s === "expired" ? "The gateway didn't pick the setup up in time." : "Setup failed.")}>
        <span>
          <StatusPill tone="danger" label="Failed" />
        </span>
      </Tooltip>
    );
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
      <span style={{ fontFamily: AN_MONO, fontSize: 12.5, color: status.username ? AN_COLORS.ink : AN_COLORS.muted }}>
        {status.username || "—"}
      </span>
      {pill && (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: AN_COLORS.muted }}>
          Setup {pill}
        </span>
      )}
    </span>
  );
}

// A gateway-managed machine runs no agent: lastActive is when the gateway last
// reached it. Same rule as the Endpoints list's agent cell.
function GatewayReach({ lastActive }) {
  const seen = Number(lastActive) > 0 ? moment.unix(Number(lastActive)) : null;
  if (!seen) return <StatusPill tone="neutral" label="Not reached yet" />;
  const online = moment().diff(seen, "minutes") <= ONLINE_WITHIN_MIN;
  return (
    <Tooltip title={`Last answered ${seen.format("YYYY-MM-DD HH:mm")}`}>
      <span>
        {online ? (
          <StatusPill tone="success" label="Online" />
        ) : (
          <StatusPill tone="danger" label={`Offline · ${seen.fromNow()}`} />
        )}
      </span>
    </Tooltip>
  );
}

const IPV4_RE = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;
const FQDN_RE = /^(?=.{1,255}$)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;

const authHeaders = () => ({ headers: { withCredentials: true, "X-Authorization": AUTH_TOKEN() } });

// Whether another endpoint already uses this address. Resolves to an error
// message, or "" when it is free.
const addressTaken = async (type, value, label) => {
  try {
    const res = await axios.post(
      `${REACT_APP_API_TENANTS_URL}/IsIPExists`,
      { orgId: ORG_ID(), tenantId: DOMAIN_ID(), [type]: value },
      authHeaders(),
    );
    const exists = res?.data?.isExists;
    if (exists === "true") return `${label} already exists`;
    if (exists === "error") return "Error in processing your request";
    return "";
  } catch {
    return "Couldn't check whether the address is in use";
  }
};

// The editable fields: the API each saves through and how it is checked.
const EDITABLE = {
  description: {
    url: "/api/v1/endpoint/updateDesc",
    validate: async (v) => (v.length > 255 ? "Description must not exceed 255 characters" : ""),
  },
  publicIp: {
    url: "/api/v1/endpoint/updatePublicIp",
    validate: async (v) => {
      if (!v) return "Public IP is required";
      if (!IPV4_RE.test(v)) return "IP address range must be 0.0.0.0 to 255.255.255.255";
      return addressTaken("publicIp", v, "Public IP");
    },
  },
  privateIp: {
    url: "/api/v1/endpoint/updatePrivateIp",
    validate: async (v) => {
      if (!v) return "Private IP is required";
      if (!IPV4_RE.test(v)) return "IP address range must be 0.0.0.0 to 255.255.255.255";
      return addressTaken("privateIp", v, "Private IP");
    },
  },
  fqdn: {
    url: "/api/v1/endpoint/updateFqdn",
    validate: async (v) => {
      if (!v) return "FQDN is required";
      if (!FQDN_RE.test(v)) return "Enter a valid FQDN (e.g., example.com)";
      return addressTaken("fqdn", v, "FQDN");
    },
  },
};

function InstanceSummaryCard({ endPoints, onPatch, adminStatus }) {
  const { hostName, description, privateIp, publicIp, fqdn, status, lastActive, osname, osName, osDetail, instanceId, jumpServerId } =
    endPoints || {};

  // A gateway-managed machine runs no agent, so its heartbeat says when the
  // gateway last reached it. Name the gateway, as the Endpoints list does.
  const [gatewayName, setGatewayName] = useState(null);
  useEffect(() => {
    setGatewayName(null);
    if (!jumpServerId) return undefined;
    let alive = true;
    findJumpServer({
      jumpServerId,
      onSuccess: (g) => alive && setGatewayName(g?.serverName || null),
      onFailure: () => {},
    });
    return () => {
      alive = false;
    };
  }, [jumpServerId]);

  // Which field is being edited (one at a time).
  const [editing, setEditing] = useState(null);

  const save = async (field, value) => {
    const spec = EDITABLE[field];
    const unchanged = String(value) === String(endPoints?.[field] ?? "");
    const error = unchanged ? "" : await spec.validate(value);
    if (error) return error;
    if (!unchanged) {
      try {
        await axios.post(
          `${REACT_APP_API_TENANTS_URL}${spec.url}`,
          { tenantId: DOMAIN_ID(), orgId: ORG_ID(), endpointId: instanceId, [field]: value },
          authHeaders(),
        );
      } catch (err) {
        return err?.response?.data?.message || err?.response?.data?.error || "Couldn't save the change";
      }
      onPatch({ [field]: value });
    }
    setEditing(null);
    return "";
  };

  const editable = (field, label, value, opts = {}) => (
    <EditableValue
      label={label}
      value={value}
      mono={opts.mono}
      multiline={opts.multiline}
      editing={editing === field}
      onEdit={() => setEditing(field)}
      onCancel={() => setEditing(null)}
      onSave={(v) => save(field, v)}
    />
  );

  const rows = [
    ["Host name", <span style={{ fontWeight: 500 }}>{hostName || "—"}</span>],
    ["Description", editable("description", "Description", description, { multiline: true })],
    ["OS", osDetail || osLabel(osname || osName) || "—"],
    ["Private IP", editable("privateIp", "Private IP", privateIp, { mono: true })],
    ["Public IP", editable("publicIp", "Public IP", publicIp, { mono: true })],
    ["FQDN", editable("fqdn", "FQDN", fqdn, { mono: true })],
    ["Status", status ? <EndpointStatusPill status={status} /> : "—"],
    ...(adminStatus ? [["Admin account", <AdminAccountValue status={adminStatus} />]] : []),
    [
      jumpServerId ? "Reachability" : "Agent status",
      jumpServerId ? (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, color: AN_COLORS.ink2 }}>
            Managed via gateway <b style={{ fontWeight: 500 }}>{gatewayName || `#${jumpServerId}`}</b>
          </span>
          <GatewayReach lastActive={lastActive} />
        </span>
      ) : (
        <AgentHeartbeat lastActive={lastActive} />
      ),
    ],
  ];

  return (
    <div style={{ ...card, padding: 18 }}>
      <PanelHead title="Summary" />
      <dl
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(110px, 160px) minmax(0, 1fr)",
          columnGap: 24,
          rowGap: 0,
          margin: 0,
        }}
      >
        {rows.map(([label, value], i) => (
          <React.Fragment key={label}>
            <dt
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: AN_COLORS.muted,
                padding: "10px 0",
                borderTop: i ? `1px solid ${AN_COLORS.line2}` : "none",
              }}
            >
              {label}
            </dt>
            <dd
              style={{
                margin: 0,
                fontSize: 13,
                color: AN_COLORS.ink,
                padding: "8px 0",
                minHeight: 38,
                display: "flex",
                alignItems: "center",
                minWidth: 0,
                borderTop: i ? `1px solid ${AN_COLORS.line2}` : "none",
              }}
            >
              {value}
            </dd>
          </React.Fragment>
        ))}
      </dl>
    </div>
  );
}

// A value with a pencil; in edit mode an input with save / cancel. `onSave`
// resolves to an error message, or "" once saved.
function EditableValue({ label, value, mono, multiline, editing, onEdit, onCancel, onSave }) {
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setDraft(value || "");
      setError("");
    }
  }, [editing, value]);

  if (!editing) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0 }}>
        <span
          style={{
            fontFamily: mono ? AN_MONO : undefined,
            fontSize: mono ? 12.5 : 13,
            color: value ? AN_COLORS.ink : AN_COLORS.muted,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {value || "—"}
        </span>
        <Tooltip title={`Edit ${label}`}>
          <button type="button" aria-label={`Edit ${label}`} onClick={onEdit} style={iconButtonStyle()}>
            <Pencil size={13} />
          </button>
        </Tooltip>
      </span>
    );
  }

  const submit = async () => {
    if (saving) return;
    setSaving(true);
    const err = await onSave(draft.trim());
    setSaving(false);
    setError(err || "");
  };
  const onKeyDown = (e) => {
    if (e.key === "Escape") onCancel();
    else if (e.key === "Enter" && (!multiline || e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      submit();
    }
  };
  const inputProps = {
    value: draft,
    autoFocus: true,
    disabled: saving,
    status: error ? "error" : undefined,
    onChange: (e) => {
      setDraft(e.target.value);
      if (error) setError("");
    },
    onKeyDown,
  };

  return (
    <div style={{ display: "grid", gap: 4, width: "100%", maxWidth: 440 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 4 }}>
        {multiline ? (
          <Input.TextArea {...inputProps} autoSize={{ minRows: 1, maxRows: 6 }} />
        ) : (
          <Input {...inputProps} style={{ fontFamily: mono ? AN_MONO : undefined }} />
        )}
        <Tooltip title="Save">
          <button
            type="button"
            aria-label={`Save ${label}`}
            onClick={submit}
            disabled={saving}
            style={{ ...iconButtonStyle(), color: AN_COLORS.success }}
          >
            {saving ? <Spin size="small" /> : <Check size={15} />}
          </button>
        </Tooltip>
        <Tooltip title="Cancel">
          <button
            type="button"
            aria-label={`Cancel editing ${label}`}
            onClick={onCancel}
            disabled={saving}
            style={iconButtonStyle()}
          >
            <X size={15} />
          </button>
        </Tooltip>
      </div>
      {error && <span style={{ fontSize: 12, color: AN_COLORS.danger }}>{error}</span>}
    </div>
  );
}
